import React, { useState, useEffect } from 'react';
import { CompositeView, SimpleView, GridView, FilterWidget, Widget, Button } from '@digital/ui';
import { Table, Select } from 'antd';
import { SearchButton } from '@app/components/FormButtons';
import {
  getPendenciasGeral,
  exportarPendenciasGeral,
  PendenciaGeralItem,
  PendenciasGeralFiltros,
  PaginationResult,
} from '../../api/pendenciaDocumental';

const PendenciasDeloitte = () => {
  const [paginacao, setPaginacao] = useState<PaginationResult<PendenciaGeralItem> | null>(null);
  const [filtros, setFiltros] = useState<PendenciasGeralFiltros>({
    numerosContrato: [],
    competenciasDocumento: [],
    nomesDocumento: [],
    tiposInconsistencia: [],
  });
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [pageAtual, setPageAtual] = useState(1);
  const [sizeAtual, setSizeAtual] = useState(20);

  const [filtroContrato, setFiltroContrato] = useState<number | undefined>(undefined);
  const [filtroCompetencia, setFiltroCompetencia] = useState<string | undefined>(undefined);
  const [filtroNomeDoc, setFiltroNomeDoc] = useState<string | undefined>(undefined);
  const [filtroTipoIncons, setFiltroTipoIncons] = useState<string | undefined>(undefined);

  const buscar = (
    contrato: number | undefined,
    competencia: string | undefined,
    nomeDoc: string | undefined,
    tipoIncons: string | undefined,
    page: number,
    size: number,
  ) => {
    setLoading(true);
    getPendenciasGeral({ numeroContrato: contrato, competenciaDocumento: competencia, nomeDocumento: nomeDoc, tipoInconsistencia: tipoIncons, page, size })
      .then((resp: any) => {
        const data = resp && resp.data ? resp.data : resp;
        if (data) {
          setPaginacao(data.pendencias || null);
          if (data.filtros) setFiltros(data.filtros);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    buscar(undefined, undefined, undefined, undefined, 1, 20);
  }, []);

  const handleFiltrar = () => {
    setPageAtual(1);
    buscar(filtroContrato, filtroCompetencia, filtroNomeDoc, filtroTipoIncons, 1, sizeAtual);
  };

  const handleExportar = () => {
    setExportando(true);
    exportarPendenciasGeral({
      numeroContrato: filtroContrato,
      competenciaDocumento: filtroCompetencia,
      nomeDocumento: filtroNomeDoc,
      tipoInconsistencia: filtroTipoIncons,
    }).finally(function() { setExportando(false); });
  };

  const handleLimpar = () => {
    setFiltroContrato(undefined);
    setFiltroCompetencia(undefined);
    setFiltroNomeDoc(undefined);
    setFiltroTipoIncons(undefined);
    setPageAtual(1);
    buscar(undefined, undefined, undefined, undefined, 1, sizeAtual);
  };

  const handleTableChange = (p: any) => {
    setPageAtual(p.current);
    setSizeAtual(p.pageSize);
    buscar(filtroContrato, filtroCompetencia, filtroNomeDoc, filtroTipoIncons, p.current, p.pageSize);
  };

  const items = paginacao ? paginacao.items : [];

  const columns = [
    {
      title: 'Contrato',
      dataIndex: 'numeroContrato',
      key: 'numeroContrato',
      width: 90,
      sorter: (a: PendenciaGeralItem, b: PendenciaGeralItem) => a.numeroContrato - b.numeroContrato,
    },
    {
      title: 'Comp. Monitoramento',
      dataIndex: 'competenciaMonitoramento',
      key: 'competenciaMonitoramento',
      width: 130,
    },
    {
      title: 'Comp. Documento',
      dataIndex: 'competenciaDocumento',
      key: 'competenciaDocumento',
      width: 110,
    },
    {
      title: 'Documento',
      dataIndex: 'nomeDocumento',
      key: 'nomeDocumento',
    },
    {
      title: 'Tipo Inconsistência',
      dataIndex: 'tipoInconsistencia',
      key: 'tipoInconsistencia',
      width: 140,
    },
    {
      title: 'Notif.',
      dataIndex: 'quantidadeNotificacoes',
      key: 'quantidadeNotificacoes',
      width: 70,
      sorter: (a: PendenciaGeralItem, b: PendenciaGeralItem) => a.quantidadeNotificacoes - b.quantidadeNotificacoes,
    },
    {
      title: 'Crítico',
      dataIndex: 'critico',
      key: 'critico',
      width: 80,
      render: (v: boolean) => (v ? <span style={{ color: 'red' }}>Sim</span> : 'Não'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: () => 'Não Conforme',
    },
  ];

  return (
    <CompositeView>
      <SimpleView>
        <GridView onSubmit={handleFiltrar}>
          <FilterWidget
            active={[]}
            actions={[
              <SearchButton type="primary" key="buscar" onClick={handleFiltrar} />,
              <Button key="limpar" onClick={handleLimpar} style={{ marginLeft: 8 }}>Limpar</Button>,
              <Button key="exportar" onClick={handleExportar} loading={exportando} style={{ marginLeft: 8 }} icon="download">Exportar</Button>,
            ]}
            title="Filtros">
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', padding: '8px 0' }}>
              <div>
                <div style={{ marginBottom: 4, fontSize: 12 }}>Nº Contrato</div>
                <Select
                  showSearch
                  allowClear
                  placeholder="Todos"
                  style={{ width: 120 }}
                  value={filtroContrato}
                  onChange={(v: any) => setFiltroContrato(v !== undefined ? Number(v) : undefined)}>
                  {filtros.numerosContrato.map(n => (
                    <Select.Option key={n} value={n}>{n}</Select.Option>
                  ))}
                </Select>
              </div>
              <div>
                <div style={{ marginBottom: 4, fontSize: 12 }}>Comp. Documento</div>
                <Select
                  showSearch
                  allowClear
                  placeholder="Todas"
                  style={{ width: 130 }}
                  value={filtroCompetencia}
                  onChange={(v: any) => setFiltroCompetencia(v || undefined)}>
                  {filtros.competenciasDocumento.map(c => (
                    <Select.Option key={c} value={c}>{c}</Select.Option>
                  ))}
                </Select>
              </div>
              <div>
                <div style={{ marginBottom: 4, fontSize: 12 }}>Documento</div>
                <Select
                  showSearch
                  allowClear
                  placeholder="Todos"
                  style={{ width: 220 }}
                  value={filtroNomeDoc}
                  onChange={(v: any) => setFiltroNomeDoc(v || undefined)}>
                  {filtros.nomesDocumento.map(d => (
                    <Select.Option key={d} value={d}>{d}</Select.Option>
                  ))}
                </Select>
              </div>
              <div>
                <div style={{ marginBottom: 4, fontSize: 12 }}>Tipo Inconsistência</div>
                <Select
                  showSearch
                  allowClear
                  placeholder="Todos"
                  style={{ width: 180 }}
                  value={filtroTipoIncons}
                  onChange={(v: any) => setFiltroTipoIncons(v || undefined)}>
                  {filtros.tiposInconsistencia.map(t => (
                    <Select.Option key={t} value={t}>{t}</Select.Option>
                  ))}
                </Select>
              </div>
            </div>
          </FilterWidget>
          <Widget title={'Pendências Documentais (Deloitte)' + (paginacao ? ' — ' + paginacao.totalItems + ' registros' : '')}>
            <Table
              className="no-bold-table"
              columns={columns}
              dataSource={items}
              rowKey={(r: PendenciaGeralItem, i: any) => r.numeroContrato + '-' + r.competenciaDocumento + '-' + r.nomeDocumento + '-' + i}
              size="small"
              loading={loading}
              scroll={{ x: 1000 }}
              onChange={handleTableChange}
              pagination={{
                current: pageAtual,
                pageSize: sizeAtual,
                total: paginacao ? paginacao.totalItems : 0,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50'],
              }}
              locale={{ emptyText: 'Nenhuma pendência encontrada.' }}
            />
          </Widget>
        </GridView>
      </SimpleView>
    </CompositeView>
  );
};

export default PendenciasDeloitte;
